import React, { useState } from "react";
import api, { CLP, formatApiErrorDetail } from "@/lib/api";
import { Field, inputCls, PrimaryButton, SecondaryButton } from "@/components/ui-kit";
import CustomerSelect from "@/components/CustomerSelect";
import { Plus, X, Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";

const toBase64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

/**
 * Modal reutilizable para crear una cotización. Puede pre-llenarse con un cliente.
 * Props:
 *  - defaultCustomerId (opcional): pre-selecciona un cliente.
 *  - defaultTitle (opcional): pre-llena el título.
 *  - onClose: callback al cerrar.
 *  - onCreated: callback tras crear (recibe la cotización creada).
 */
export default function QuoteForm({ defaultCustomerId, defaultTitle = "", onClose, onCreated }) {
  const [data, setData] = useState({
    customer_id: defaultCustomerId || "",
    title: defaultTitle,
    tax_rate: 19,
    status: "draft",
    valid_until: "",
    items: [{ description: "", quantity: 1, unit_price: 0 }],
    notes: "",
    photos: [],
  });
  const [saving, setSaving] = useState(false);

  const handlePhotos = async (files) => {
    if (!files?.length) return;
    const list = Array.from(files);
    const encoded = await Promise.all(list.map(async (f) => ({ data: await toBase64(f), caption: "" })));
    setData((d) => ({ ...d, photos: [...(d.photos || []), ...encoded] }));
  };
  const updatePhotoCaption = (idx, caption) => {
    setData((d) => ({ ...d, photos: d.photos.map((p, i) => (i === idx ? { ...p, caption } : p)) }));
  };
  const removePhoto = (idx) => {
    setData((d) => ({ ...d, photos: d.photos.filter((_, i) => i !== idx) }));
  };

  const subtotal = data.items.reduce((a, it) => a + (Number(it.quantity || 0) * Number(it.unit_price || 0)), 0);
  const tax = subtotal * (data.tax_rate / 100);
  const total = subtotal + tax;

  const updateItem = (idx, k, v) => {
    const items = [...data.items];
    items[idx] = { ...items[idx], [k]: v };
    setData({ ...data, items });
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...data,
        valid_until: data.valid_until || null,
        items: data.items.filter((i) => i.description.trim()).map((i) => ({ ...i, quantity: Number(i.quantity), unit_price: Number(i.unit_price) })),
      };
      const { data: q } = await api.post("/quotes", payload);
      toast.success("Cotización creada");
      onCreated && onCreated(q);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl border-t-4 border-[#FF5A00] max-h-[90vh] overflow-y-auto" data-testid="quote-form-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-[#FF5A00]">// Nueva cotización</div>
            <div className="font-display font-bold text-xl">Crear cotización</div>
          </div>
          <button type="button" onClick={onClose} className="hover:bg-neutral-100 p-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Cliente *">
              <CustomerSelect
                value={data.customer_id}
                onChange={(id) => setData({ ...data, customer_id: id })}
                required
                testid="quote-customer-select"
              />
            </Field>
            <Field label="Título *">
              <input required data-testid="quote-title-input" value={data.title} onChange={(e) => setData({ ...data, title: e.target.value })} className={inputCls} />
            </Field>
            <Field label="IVA (%)">
              <input type="number" value={data.tax_rate} onChange={(e) => setData({ ...data, tax_rate: Number(e.target.value) })} className={inputCls} />
            </Field>
            <Field label="Válida hasta">
              <input type="date" value={data.valid_until} onChange={(e) => setData({ ...data, valid_until: e.target.value })} className={inputCls} />
            </Field>
          </div>

          <div>
            <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500 mb-2">Ítems</div>
            <div className="border border-neutral-200">
              <div className="grid grid-cols-[1fr_80px_120px_120px_40px] gap-2 px-3 py-2 bg-neutral-50 border-b border-neutral-200 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-neutral-500">
                <div>Descripción</div><div className="text-right">Cant.</div><div className="text-right">Precio</div><div className="text-right">Total</div><div />
              </div>
              {data.items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_80px_120px_120px_40px] gap-2 px-3 py-2 border-b border-neutral-100 items-center">
                  <input data-testid={`quote-item-desc-${i}`} value={it.description} onChange={(e) => updateItem(i, "description", e.target.value)} className={inputCls} placeholder="Reparación, repuesto…" />
                  <input type="number" min="0" step="0.1" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} className={`${inputCls} text-right`} />
                  <input type="number" min="0" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} className={`${inputCls} text-right`} />
                  <div className="font-mono-kpi text-right text-sm">{CLP(Number(it.quantity || 0) * Number(it.unit_price || 0))}</div>
                  <button type="button" onClick={() => setData({ ...data, items: data.items.filter((_, idx) => idx !== i) })} className="hover:bg-neutral-100 p-1.5"><X className="w-3 h-3" /></button>
                </div>
              ))}
              <button type="button" data-testid="quote-add-item" onClick={() => setData({ ...data, items: [...data.items, { description: "", quantity: 1, unit_price: 0 }] })} className="w-full text-xs font-mono-kpi uppercase tracking-[0.15em] text-[#FF5A00] hover:bg-[#FF5A00]/5 py-2 border-t border-neutral-100">
                <Plus className="w-3 h-3 inline mr-1" /> Agregar ítem
              </button>
            </div>
          </div>

          <div className="bg-neutral-50 border border-neutral-200 p-4 grid grid-cols-3 gap-3 text-sm">
            <div><div className="text-xs text-neutral-500">Subtotal</div><div className="font-mono-kpi font-bold">{CLP(subtotal)}</div></div>
            <div><div className="text-xs text-neutral-500">IVA ({data.tax_rate}%)</div><div className="font-mono-kpi font-bold">{CLP(tax)}</div></div>
            <div><div className="text-xs text-neutral-500">Total</div><div className="font-mono-kpi font-bold text-[#FF5A00] text-lg">{CLP(total)}</div></div>
          </div>

          <Field label="Notas">
            <textarea rows={2} value={data.notes} onChange={(e) => setData({ ...data, notes: e.target.value })} className={inputCls} />
          </Field>

          {/* Fotos adjuntas (opcional) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="font-mono-kpi text-[10px] uppercase tracking-[0.2em] text-neutral-500">Fotos adjuntas ({data.photos.length})</div>
              <label className="cursor-pointer inline-flex items-center gap-1.5 font-mono-kpi text-[10px] uppercase tracking-[0.15em] text-[#FF5A00] hover:text-[#E04A00]" data-testid="quote-photos-add">
                <Camera className="w-3.5 h-3.5" />Agregar fotos
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="quote-photos-input"
                  onChange={(e) => handlePhotos(e.target.files)}
                />
              </label>
            </div>
            {data.photos.length === 0 ? (
              <div className="border border-dashed border-neutral-300 px-3 py-4 text-xs text-neutral-500 text-center">
                Sin fotos. Adjunta imágenes para incluirlas en la cotización PDF.
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {data.photos.map((p, i) => (
                  <div key={i} className="border border-neutral-200 p-1.5" data-testid={`quote-photo-${i}`}>
                    <img src={p.data} alt="" className="w-full h-24 object-cover mb-1.5" />
                    <input
                      value={p.caption || ""}
                      onChange={(e) => updatePhotoCaption(i, e.target.value)}
                      placeholder="Descripción…"
                      className="w-full text-[11px] border border-neutral-200 px-2 py-1"
                    />
                    <button type="button" onClick={() => removePhoto(i)} data-testid={`quote-photo-remove-${i}`} className="w-full mt-1 text-[10px] font-mono-kpi uppercase tracking-wider text-[#B71C1C] hover:bg-[#B71C1C]/5 py-1 inline-flex items-center justify-center gap-1">
                      <Trash2 className="w-3 h-3" />Quitar
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-neutral-200 bg-neutral-50">
          <SecondaryButton type="button" onClick={onClose}>Cancelar</SecondaryButton>
          <PrimaryButton type="submit" disabled={saving} data-testid="quote-form-submit">{saving ? "Creando…" : "Crear cotización"}</PrimaryButton>
        </div>
      </form>
    </div>
  );
}
